import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { SmartFilter } from '../../components/common/SmartFilter';
import withComponentAuth from '../../hoc/withComponentAuth';
import useSnackbar from '../../hooks/useSnackbar';
import {
  IdentityQueueItem,
  decideIdentityCheck,
  getIdentityDocumentViewUrl,
  getIdentityQueue,
} from '../../services/channelPartners';

const pretty = (value?: string | null) =>
  value ? value.toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : 'N/A';

const IdentityQueue = () => {
  const navigate = useNavigate();
  const { showSnackbar, SnackbarComponent } = useSnackbar();
  const [items, setItems] = useState<IdentityQueueItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [viewDoc, setViewDoc] = useState<{ url: string; contentType?: string }>({ url: '' });
  const [viewOpen, setViewOpen] = useState(false);
  const [decisionTarget, setDecisionTarget] = useState<IdentityQueueItem | null>(null);
  const [decision, setDecision] = useState<'PASSED' | 'FAILED'>('PASSED');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchQueue = useCallback(async () => {
    try {
      setLoading(true);
      setItems(await getIdentityQueue());
    } catch (error: any) {
      showSnackbar(error?.message || 'Failed to load identity queue', 'error');
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  useEffect(() => {
    fetchQueue();
    const timer = window.setInterval(fetchQueue, 30000);
    return () => window.clearInterval(timer);
  }, [fetchQueue]);

  const openDocument = async (item: IdentityQueueItem) => {
    if (!item.docId) {
      showSnackbar('No document is attached to this review', 'error');
      return;
    }
    try {
      const { signedUrl, contentType } = await getIdentityDocumentViewUrl(item.docId);
      setViewDoc({ url: signedUrl, contentType });
      setViewOpen(true);
    } catch (error: any) {
      showSnackbar(error?.message || 'Document link is no longer available', 'error');
    }
  };

  const openDecision = (item: IdentityQueueItem, result: 'PASSED' | 'FAILED') => {
    setDecisionTarget(item);
    setDecision(result);
    setReason('');
  };

  const submitDecision = async () => {
    if (!decisionTarget) return;
    if (decision === 'FAILED' && !reason.trim()) {
      showSnackbar('Rejection reason is required', 'error');
      return;
    }
    try {
      setSaving(true);
      await decideIdentityCheck(decisionTarget.checkId, {
        result: decision,
        failureReason: decision === 'FAILED' ? reason.trim() : undefined,
      });
      showSnackbar(decision === 'PASSED' ? 'Identity approved' : 'Identity rejected', 'success');
      setDecisionTarget(null);
      await fetchQueue();
    } catch (error: any) {
      showSnackbar(error?.message || 'Failed to save decision', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {SnackbarComponent}
      <SmartFilter title="Partner Identity Review" createFilter={[]} extraActions={<Button variant="outlined" onClick={fetchQueue}>Refresh</Button>} />
      <Box sx={{ p: 1, minHeight: 0 }}>
        <Paper sx={{ p: 1.5, bgcolor: '#171717', border: '1px solid #242424', borderRadius: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5} gap={1} flexWrap="wrap">
            <Box>
              <Typography color="#fff" fontWeight={600}>Manual Identity Checks</Typography>
              <Typography variant="caption" color="#64748b">Auto-refreshes every 30 seconds. Document links are short-lived.</Typography>
            </Box>
            <Chip label={`${items.length} pending`} sx={{ bgcolor: '#f59e0b22', color: '#f59e0b', fontWeight: 600 }} />
          </Box>
          <Box sx={{ overflowX: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow>
                  {['Partner', 'Phone', 'Type', 'Document', 'Uploaded', 'Actions'].map((head) => (
                    <TableCell key={head} sx={{ color: '#64748b', borderColor: '#242424' }} align={head === 'Actions' ? 'right' : 'left'}>{head}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={6} sx={{ color: '#94a3b8', borderColor: '#242424' }}>Loading identity reviews...</TableCell></TableRow>
                ) : items.length === 0 ? (
                  <TableRow><TableCell colSpan={6} sx={{ color: '#94a3b8', borderColor: '#242424' }}>No identity reviews waiting</TableCell></TableRow>
                ) : items.map((item) => (
                  <TableRow key={item.checkId} hover sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.02)' } }}>
                    <TableCell sx={{ borderColor: '#242424' }}>
                      <Typography color="#e2e8f0" fontWeight={600} fontSize={14}>{item.fullName || 'Unnamed partner'}</Typography>
                      <Button size="small" onClick={() => navigate(`/channel-partners/${item.partnerId}`)}>Open profile</Button>
                    </TableCell>
                    <TableCell sx={{ color: '#94a3b8', borderColor: '#242424' }}>{item.phoneNumber}</TableCell>
                    <TableCell sx={{ color: '#94a3b8', borderColor: '#242424' }}>{pretty(item.partnerType)}</TableCell>
                    <TableCell sx={{ color: '#94a3b8', borderColor: '#242424' }}>{pretty(item.docType)}</TableCell>
                    <TableCell sx={{ color: '#94a3b8', borderColor: '#242424' }}>{item.uploadedAt ? dayjs(item.uploadedAt).format('DD MMM YYYY, h:mm A') : 'N/A'}</TableCell>
                    <TableCell align="right" sx={{ borderColor: '#242424' }}>
                      <Box display="flex" justifyContent="flex-end" gap={1} flexWrap="wrap">
                        <Button size="small" startIcon={<VisibilityIcon />} onClick={() => openDocument(item)}>View</Button>
                        <Button size="small" color="success" startIcon={<CheckCircleOutlineIcon />} onClick={() => openDecision(item, 'PASSED')}>Approve</Button>
                        <Button size="small" color="error" startIcon={<CancelOutlinedIcon />} onClick={() => openDecision(item, 'FAILED')}>Reject</Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Paper>
      </Box>

      <Dialog open={viewOpen} onClose={() => setViewOpen(false)} maxWidth="lg" fullWidth PaperProps={{ sx: { bgcolor: '#171717', color: '#fff', border: '1px solid #242424' } }}>
        <DialogTitle>Identity Document</DialogTitle>
        <DialogContent>
          <Typography variant="caption" color="#94a3b8">This signed link expires after a few minutes. Reopen the document to fetch a fresh link.</Typography>
          <Box sx={{ mt: 1.5, height: '70vh', bgcolor: '#0f0f0f', border: '1px solid #242424', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'auto' }}>
            {viewDoc.url && (
              viewDoc.contentType?.startsWith('image/') ? (
                <img
                  src={viewDoc.url}
                  alt="Identity document"
                  style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                />
              ) : (
                <iframe title="Identity document" src={viewDoc.url} style={{ width: '100%', height: '100%', border: 0 }} />
              )
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button startIcon={<OpenInNewIcon />} onClick={() => window.open(viewDoc.url, '_blank', 'noopener,noreferrer')}>Open in new tab</Button>
          <Button onClick={() => setViewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(decisionTarget)} onClose={() => !saving && setDecisionTarget(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { bgcolor: '#171717', color: '#fff', border: '1px solid #242424' } }}>
        <DialogTitle>{decision === 'PASSED' ? 'Approve identity?' : 'Reject identity?'}</DialogTitle>
        <DialogContent>
          <Typography color="#94a3b8" mb={2}>{decisionTarget?.fullName || 'This partner'} will be marked {decision === 'PASSED' ? 'approved for identity verification.' : 'rejected for identity verification.'}</Typography>
          {decision === 'FAILED' && <TextField fullWidth multiline minRows={3} label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDecisionTarget(null)} disabled={saving}>Cancel</Button>
          <Button variant="contained" color={decision === 'PASSED' ? 'success' : 'error'} onClick={submitDecision} disabled={saving}>{saving ? 'Saving...' : 'Confirm'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default withComponentAuth(IdentityQueue);
